import React from "react";
import { useNavigate } from "react-router-dom";
import { Settings, LogOut, Plus, MessageSquare, Trash2, Bot, StickyNote, Workflow } from "lucide-react";
import Logo from "./Logo";
import NotesPanel from "./NotesPanel";
import { useAuth } from "../lib/auth-context";
import { useAgents } from "../lib/agents-context";

/**
 * Skeleton de "Conversas recentes" — barras animadas (shimmer) que aparecem
 * enquanto a lista está carregando da API. Larguras variadas pra parecer real.
 * Exportado pra ser reusado também no drawer mobile.
 */
export function ConversationsSkeleton({ count = 8 }) {
    // Larguras pseudo-aleatórias mas determinísticas (evita layout shift entre renders)
    const widths = [86, 70, 92, 64, 78, 88, 58, 74, 82, 68, 90, 62];
    return (
        <div className="flex flex-col gap-1" data-testid="sidebar-tasks-skeleton" aria-busy="true" aria-label="Carregando conversas">
            {/* Cabeçalho de grupo "fake" pra dar contexto visual */}
            <div className="px-3 py-1">
                <div className="skeleton-bar h-2 w-12 rounded" />
            </div>
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2">
                    <div
                        className="skeleton-bar h-3.5 rounded"
                        style={{ width: `${widths[i % widths.length]}%` }}
                    />
                </div>
            ))}
        </div>
    );
}

export default function Sidebar({
    onNewChat,
    tasks = [],
    loading = false,
    currentTaskId,
    onSelect,
    onLogoutRequest,
    onDelete,
}) {
    const { user, logout } = useAuth();
    const { openAgentsDialog, agents } = useAgents();
    const navigate = useNavigate();
    const [tab, setTab] = React.useState("chats"); // "chats" | "notes"

    const grouped = React.useMemo(() => groupByDate(tasks), [tasks]);

    return (
        <aside
            data-testid="sidebar"
            className="hidden md:flex flex-col w-[280px] h-screen border-r border-border bg-[#0d0d0d] sticky top-0 shrink-0"
        >
            <div className="p-5 border-b border-border">
                <div className="flex items-center gap-2 mb-5">
                    <Logo className="w-7 h-7" />
                    <span className="font-display text-base font-bold tracking-tighter">LINKIA</span>
                </div>
                <div className="flex gap-2">
                    <button
                        data-testid="new-chat-btn"
                        onClick={onNewChat}
                        className="btn-primary flex-1 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2.5} />
                        Nova Conversa
                    </button>
                    <button
                        data-testid="create-agent-btn"
                        onClick={() => openAgentsDialog("form")}
                        title="Criar agente"
                        aria-label="Criar agente"
                        className="shrink-0 w-10 h-10 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors flex items-center justify-center"
                    >
                        <Bot className="w-4.5 h-4.5" strokeWidth={2.2} />
                    </button>
                </div>
                <button
                    data-testid="my-agents-btn"
                    onClick={() => openAgentsDialog("list")}
                    className="mt-2 w-full py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 bg-[#1d1d1d] border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                    <Bot className="w-3.5 h-3.5" strokeWidth={2.2} />
                    Meus Agentes{agents.length > 0 ? ` (${agents.length})` : ""}
                </button>
                <button
                    data-testid="bots-pipeline-btn"
                    onClick={() => navigate("/bots")}
                    className="mt-2 w-full py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 bg-[#1d1d1d] border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                    <Workflow className="w-3.5 h-3.5" strokeWidth={2.2} />
                    Pipeline de Bots
                </button>
            </div>

            {/* Tabs: Conversas | Notas */}
            <SidebarTabs tab={tab} onChange={setTab} />

            <div className="flex-1 overflow-y-auto px-2 pb-4" data-testid="sidebar-tasks">
                {tab === "notes" ? (
                    <NotesPanel />
                ) : (
                    <>
                        {loading && tasks.length === 0 && <ConversationsSkeleton count={8} />}
                        {!loading && tasks.length === 0 && (
                            <div className="flex flex-col items-center justify-center gap-2 px-3 py-10 text-center text-muted-foreground">
                                <MessageSquare className="w-6 h-6 opacity-60" strokeWidth={1.5} />
                                <div className="text-xs">Sem conversas</div>
                            </div>
                        )}
                        {Object.entries(grouped).map(([label, items]) => (
                            <div key={label} className="mb-3">
                                <div className="px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground/70">{label}</div>
                                {items.map((t) => (
                                    <TaskRow
                                        key={t.id}
                                        task={t}
                                        active={currentTaskId === t.id}
                                        onSelect={() => onSelect?.(t)}
                                        onDelete={onDelete ? () => onDelete(t) : null}
                                    />
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </div>

            <div className="border-t border-border p-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full border border-border overflow-hidden bg-muted shrink-0">
                        <img src={user?.avatar_url || "/img/default-avatar.svg"} alt="" loading="lazy" className="w-full h-full object-cover" data-testid="sidebar-avatar" onError={(e) => { e.currentTarget.src = "/img/default-avatar.svg"; }} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{user?.name || "Usuário"}</div>
                        <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </div>
                    <button
                        onClick={() => navigate("/configuracoes")}
                        className="p-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        aria-label="Configurações"
                        data-testid="sidebar-settings"
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                    <button
                        data-testid="sidebar-logout"
                        onClick={() => {
                            if (onLogoutRequest) onLogoutRequest();
                            else { logout(); navigate("/login"); }
                        }}
                        className="p-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        aria-label="Sair"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </aside>
    );
}

/**
 * Tabs Conversas | Notas — usada na sidebar desktop e no drawer mobile.
 */
export function SidebarTabs({ tab, onChange }) {
    return (
        <div className="mx-3 mt-3 mb-2 grid grid-cols-2 gap-1 p-1 rounded-full bg-[#141414] border border-border" role="tablist" data-testid="sidebar-tabs">
            <button
                type="button"
                role="tab"
                aria-selected={tab === "chats"}
                onClick={() => onChange("chats")}
                data-testid="tab-chats"
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                    tab === "chats" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
            >
                <MessageSquare className="w-3.5 h-3.5" />
                Conversas
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={tab === "notes"}
                onClick={() => onChange("notes")}
                data-testid="tab-notes"
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                    tab === "notes" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
            >
                <StickyNote className="w-3.5 h-3.5" />
                Notas
            </button>
        </div>
    );
}

function TaskRow({ task, active, onSelect, onDelete }) {
    return (
        <div
            className={`group relative flex items-center w-full rounded-sm transition-colors ${
                active
                    ? "bg-muted text-foreground border-l-2 border-primary"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            }`}
        >
            <button
                type="button"
                data-testid={`sidebar-task-${task.id}`}
                onClick={onSelect}
                className="flex-1 min-w-0 text-left px-3 py-2 text-sm truncate"
                title={task.title || "Conversa sem título"}
            >
                {task.title || "Conversa sem título"}
            </button>
            {onDelete && (
                <button
                    type="button"
                    data-testid={`sidebar-task-delete-${task.id}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    aria-label="Excluir conversa"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1.5 mr-1 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
}

function groupByDate(tasks) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const yesterday = today - 24 * 3600 * 1000;
    const weekAgo = today - 7 * 24 * 3600 * 1000;

    const groups = { "Hoje": [], "Ontem": [], "Esta semana": [], "Anteriores": [] };
    for (const t of tasks) {
        const ts = new Date(t.last_message_at || t.created_at || 0).getTime();
        if (ts >= today) groups["Hoje"].push(t);
        else if (ts >= yesterday) groups["Ontem"].push(t);
        else if (ts >= weekAgo) groups["Esta semana"].push(t);
        else groups["Anteriores"].push(t);
    }
    return Object.fromEntries(Object.entries(groups).filter(([, v]) => v.length > 0));
}
